import React, { useRef, useState } from "react";
import { Upload, X, FileText, Loader2 } from "lucide-react";
import api from "@/lib/api";
import { toast } from "sonner";

/**
 * FileUpload — multi-file upload that calls /api/files/upload per file and
 * stores resulting FileRef objects in `value` via onChange.
 *
 * Props:
 *  - value: FileRef[]
 *  - onChange: (FileRef[]) => void
 */
export default function FileUpload({ value = [], onChange, testid = "file-upload" }) {
  const inputRef = useRef(null);
  const [uploading, setUploading] = useState(false);

  const handleFiles = async (files) => {
    if (!files || files.length === 0) return;
    setUploading(true);
    const next = [...value];
    for (const f of Array.from(files)) {
      try {
        const fd = new FormData();
        fd.append("file", f);
        const { data } = await api.post("/files/upload", fd, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        next.push({
          id: data.id,
          path: data.path,
          filename: data.filename,
          content_type: data.content_type,
          size: data.size,
        });
      } catch (e) {
        toast.error(`Upload failed for ${f.name}`);
      }
    }
    onChange?.(next);
    setUploading(false);
    if (inputRef.current) inputRef.current.value = "";
  };

  const remove = (idx) => {
    const next = value.filter((_, i) => i !== idx);
    onChange?.(next);
  };

  return (
    <div className="space-y-3" data-testid={testid}>
      <button
        type="button"
        onClick={() => inputRef.current?.click()}
        className="w-full border-2 border-dashed border-white/15 hover:border-white/30 bg-zinc-950/50 rounded-lg p-6 flex flex-col items-center justify-center text-zinc-400 transition-colors"
        data-testid={`${testid}-trigger`}
      >
        {uploading ? (
          <Loader2 className="w-5 h-5 animate-spin mb-2" />
        ) : (
          <Upload className="w-5 h-5 mb-2" strokeWidth={1.5} />
        )}
        <div className="text-sm text-zinc-300">
          {uploading ? "Uploading…" : "Click to upload files"}
        </div>
        <div className="text-xs text-zinc-500 mt-1">Multiple files supported</div>
      </button>
      <input
        ref={inputRef}
        type="file"
        multiple
        className="hidden"
        onChange={(e) => handleFiles(e.target.files)}
        data-testid={`${testid}-input`}
      />
      {value.length > 0 && (
        <div className="space-y-2">
          {value.map((f, idx) => (
            <div
              key={f.id || idx}
              className="flex items-center justify-between bg-zinc-900/60 border border-white/10 rounded-md px-3 py-2"
              data-testid={`${testid}-item-${idx}`}
            >
              <div className="flex items-center gap-2 min-w-0">
                <FileText className="w-4 h-4 text-zinc-400 shrink-0" />
                <span className="text-sm text-zinc-200 truncate">{f.filename}</span>
                <span className="text-xs text-zinc-500 shrink-0">
                  {f.size ? `${(f.size / 1024).toFixed(1)} KB` : ""}
                </span>
              </div>
              <button
                type="button"
                onClick={() => remove(idx)}
                className="p-1 text-zinc-500 hover:text-red-400"
                aria-label="Remove file"
                data-testid={`${testid}-remove-${idx}`}
              >
                <X className="w-4 h-4" />
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
